import React, { useState, useEffect } from 'react'
import { Classes, EditableText, Intent } from '@blueprintjs/core'
import { Dialog } from '@ui'
import { Tooltip2 } from '@blueprintjs/popover2'
import { NavigateFn } from '@reach/router'
import {
  Avatar,
  EmployeeWithDetailsPopover,
  Flex,
  IconButton,
  Confirmation,
  Toaster,
  PrettyDate,
  ColorSelect,
} from '@ui'
import { useReload } from '@hooks/reloadContext'
import EmployeesManageSection from './EmployeesManageSection'
import useNeighborhood, { UseNeighborhoodHook } from './hooks/useNeighborhood'
import SpacesManageSection from './SpacesManageSection'
import api, { FormErrors, Role } from '@api'
import permissions, { Permissions, PermissionsType, IPermissionsOnNeighborhood } from '@permissions'
import { useUser } from '@hooks'
import track from '@track'
import Event from '@track/Event'
import { ApolloError } from 'apollo-boost'
import css from './neighborhoodDetailsDialog.module.scss'

interface NeighborhoodDetailsDialogProps {
  neighborhoodId: string
  navigate?: NavigateFn
}

interface DialogToolbarProps {
  onClose?: () => void
  onDelete?: () => Promise<any>
}

interface NeighborhoodDetails {
  name: string
}

type NeighborhoodDetailsErrors = FormErrors<NeighborhoodDetails>

const NDD: React.FunctionComponent<NeighborhoodDetailsDialogProps> = ({
  neighborhoodId,
  navigate,
}: NeighborhoodDetailsDialogProps) => {
  const neighborhood = useNeighborhood(neighborhoodId)

  const onClose = () => navigate?.(window.location.pathname)

  const toolbarProps: DialogToolbarProps = {}

  const reload = useReload()
  const { user } = useUser()

  toolbarProps.onClose = onClose

  if (user) {
    if (
      permissions.onFloor(user, neighborhood.item?.floor).canDeleteNeighborhood ||
      permissions.onNeighborhood(user, neighborhood.item ? [neighborhood.item] : null).canDelete
    ) {
      toolbarProps.onDelete = () => {
        return api.neighborhood
          .remove(neighborhoodId)
          .then(() => {
            onClose()
            setTimeout(reload.neighborhoods, 50)
          })
          .catch((error) => {
            Toaster.dismissAll()
            Toaster.fail({ error })
            reload.neighborhoods()
          })
      }
    }
  }

  return (
    <Dialog
      isOpen
      usePortal
      style={{ width: 900, position: 'relative' }}
      canEscapeKeyClose={false}
      transitionDuration={2000}
      isCloseButtonShown={false}
      onClose={onClose}
      toolbar={<DialogToolbar {...toolbarProps} />}
      body={<Details neighborhood={neighborhood} />}
    />
  )
}

function Skeleton({ withoutHeroes }: { withoutHeroes: boolean }) {
  const placeholder = <span className={Classes.SKELETON}>Loooooong placeholder text</span>

  return (
    <>
      <Flex align="center" style={{ marginBottom: 20, color: 'var(--grey1)' }}>
        <Flex align="center">
          <Avatar skeleton size={Avatar.Size.XS} />
          <Flex direction="column">
            <span style={{ padding: 10, fontSize: 18 }}>{placeholder}</span>
          </Flex>
        </Flex>
      </Flex>
      <Flex align="center">
        <span className={Classes.SKELETON} style={{ marginRight: 20, height: 30, width: 30 }}>
          DD
        </span>
        <span style={{ fontSize: 24 }}>{placeholder}</span>
      </Flex>

      <Flex direction="column">
        <span style={{ padding: 10, fontSize: 14 }}>{placeholder}</span>
      </Flex>
      {!withoutHeroes && (
        <Flex style={{ marginTop: 20 }} justify="space-between">
          <div className={Classes.SKELETON} style={{ height: 50, width: 100 }}>
            Placeholder
          </div>
          <div className={Classes.SKELETON} style={{ height: 50, width: 100 }}>
            Placeholder
          </div>
          <div className={Classes.SKELETON} style={{ height: 50, width: 100 }}>
            Placeholder
          </div>
          <div className={Classes.SKELETON} style={{ height: 50, width: 100 }}>
            Placeholder
          </div>
        </Flex>
      )}
    </>
  )
}

function Details({ neighborhood }: { neighborhood: UseNeighborhoodHook }) {
  const [name, setName] = useState('')
  const [capacity, setCapacity] = useState('0')
  const [color, setColor] = useState('')
  const [errors, setErrors] = useState<NeighborhoodDetailsErrors>({})

  const reload = useReload()
  const { user } = useUser()

  const isDeptAdmin = user?.employee.role === Role.DEPARTMENT_ADMIN

  useEffect(() => {
    if (neighborhood.item) {
      setCapacity(neighborhood.item.counters.maxEmployees)
      setName(neighborhood.item.name)
      setColor(neighborhood.item.color)
    }
  }, [neighborhood.item])

  const handleErrors = ({ kind, graphQLErrors }: any) => {
    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: NeighborhoodDetailsErrors = graphQLErrors.reduce(
        (result: NeighborhoodDetailsErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const pushColor = (color: string, enableUndo = true) => {
    const oldColor = neighborhood.item ? neighborhood.item.color : ''

    track.neighborhoodDialog({
      eventName: Event.INLINE_ACTION,
      props: {
        actionName: 'Change Color',
        actionValue: null,
      },
    })

    return api.neighborhood
      .update({
        id: neighborhood.item ? neighborhood.item.id : '',
        color,
      })
      .then(() => {
        setColor(color)
        if (!enableUndo) return
        Toaster.success({
          message: 'Neighborhood color changed',
          action: {
            onClick: () => {
              pushColor(oldColor, false)
              track.neighborhoodDialog({
                eventName: Event.UNDO_CLICK,
                props: {
                  component: 'Color picker',
                  actionName: 'Undo Color Change',
                },
              })
            },
            text: 'Undo',
          },
        })
      })
      .then(() => reload.neighborhoods())
      .catch((e) =>
        Toaster.fail({
          message: e.message,
        })
      )
  }

  return (
    <div className={Classes.DIALOG_BODY}>
      {!neighborhood.item && <Skeleton withoutHeroes={isDeptAdmin} />}
      <Flex align="center" style={{ marginBottom: 20, color: 'var(--grey1)' }}>
        {neighborhood.item?.createdBy && (
          <>
            <Avatar
              name={neighborhood.item.createdBy.employee.name}
              assets={neighborhood.item.createdBy.employee.assets}
              size={Avatar.Size.XS}
            />
            <EmployeeWithDetailsPopover
              buttonProps={{ small: true, minimal: true }}
              employee={neighborhood.item.createdBy.employee}
            />
          </>
        )}
        {neighborhood.item?.createdAt && (
          <>
            <span style={{ paddingRight: '0.5em' }}>created on</span>
            <PrettyDate value={neighborhood.item?.createdAt} />
          </>
        )}
      </Flex>
      {neighborhood.item && (
        <>
          <Permissions type={PermissionsType.NEIGHBORHOOD} on={[neighborhood.item]}>
            {(permissions) => {
              const { canUpdate } = permissions as IPermissionsOnNeighborhood

              return (
                <div className="flex item-center">
                  <span>
                    <ColorSelect
                      buttonProps={{
                        small: true,
                        minimal: true,
                      }}
                      disabled={!canUpdate}
                      value={color}
                      onChange={pushColor}
                    />
                  </span>
                  <div className="text-2xl ml-1" style={{ width: '90%' }}>
                    <EditableText
                      multiline={true}
                      disabled={!canUpdate}
                      intent={errors.name ? Intent.DANGER : undefined}
                      value={name}
                      className={!neighborhood.item ? Classes.SKELETON : ''}
                      onChange={setName}
                      onConfirm={() => {
                        if (neighborhood.item) {
                          if (name !== neighborhood.item.name) {
                            const oldName = neighborhood.item.name

                            api.neighborhood
                              .update({ id: neighborhood.item.id, name })
                              .then(() =>
                                Toaster.success({
                                  message: 'Neighborhood name changed',
                                  action: {
                                    onClick: () => {
                                      if (neighborhood.item) {
                                        setName(oldName)

                                        api.neighborhood
                                          .update({
                                            id: neighborhood.item.id,
                                            name: oldName,
                                          })
                                          .catch((error) => Toaster.fail({ error }))
                                      }

                                      track.neighborhoodDialog({
                                        eventName: Event.UNDO_CLICK,
                                        props: {
                                          component: 'Name input',
                                          actionName: 'Undo Name Change',
                                        },
                                      })
                                    },
                                    text: 'Undo',
                                  },
                                })
                              )
                              .then(() => reload.neighborhoods())
                              .catch((error: ApolloError) => {
                                handleErrors(error)
                                Toaster.fail({
                                  error,
                                })
                              })

                            track.neighborhoods({
                              eventName: Event.INLINE_ACTION,
                              props: {
                                actionName: 'Rename neighborhood',
                                actionValue: null,
                              },
                            })
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )
            }}
          </Permissions>

          <div className="ml-2 mt-2 text-base text-grey-600">
            {`${neighborhood.item.floor.name}, ${neighborhood.item.floor.building.name}, ${neighborhood.item.floor.building.city}`}
          </div>
          {!isDeptAdmin && (
            <div className={css.counters_container}>
              <Tooltip2
                position="bottom"
                content={
                  <>
                    <div>
                      This is the ratio of the actual count of employees to the count of the actual,
                      physical seats.
                    </div>
                    <div>The number of employees can be greater than the number of seats.</div>
                  </>
                }
              >
                <EmployeesAndSeatsCounter
                  employees={neighborhood.item.counters.employees}
                  workstations={neighborhood.item.counters.workstations}
                />
              </Tooltip2>
              <Tooltip2 content="The number of employees divided by the number of seats.">
                <SharingRatioCounter value={neighborhood.item.counters.sharingRatio} />
              </Tooltip2>

              <div style={{ maxWidth: 80, marginTop: '-8px' }}>
                <div className={css.font_weight500wrapper}>
                  <Permissions type={PermissionsType.NEIGHBORHOOD} on={[neighborhood.item]}>
                    {(permissions) => {
                      const { canUpdate } = permissions as IPermissionsOnNeighborhood

                      return (
                        <EditableText
                          disabled={!canUpdate}
                          multiline={false}
                          value={capacity}
                          onChange={(val) => {
                            if (!/[^0-9]/.test(val)) {
                              setCapacity(val)
                            }
                          }}
                          onConfirm={() => {
                            if (neighborhood.item) {
                              if (
                                capacity.length &&
                                capacity !== neighborhood.item.counters.maxEmployees
                              ) {
                                const oldCapacity = neighborhood.item.counters.maxEmployees

                                api.neighborhood
                                  .update({
                                    id: neighborhood.item.id,
                                    maxEmployees: Number(capacity),
                                  })
                                  .then(() =>
                                    Toaster.success({
                                      message: 'Capacity changed',
                                      action: {
                                        onClick: () => {
                                          if (neighborhood.item) {
                                            setCapacity(oldCapacity)

                                            api.neighborhood
                                              .update({
                                                id: neighborhood.item.id,
                                                maxEmployees: Number(oldCapacity),
                                              })
                                              .catch((error) => Toaster.fail({ error }))
                                          }

                                          track.neighborhoodDialog({
                                            eventName: Event.UNDO_CLICK,
                                            props: {
                                              component: 'Capacity input',
                                              actionName: 'Undo Capacity Change',
                                            },
                                          })
                                        },
                                        text: 'Undo',
                                      },
                                    })
                                  )
                                  .then(() => reload.neighborhoods())
                                  .catch((error) => {
                                    Toaster.dismissAll()
                                    Toaster.fail({ error })
                                    reload.neighborhoods()
                                  })
                              }

                              if (!capacity.length) {
                                setCapacity(neighborhood.item.counters.maxEmployees)
                              }

                              track.neighborhoodDialog({
                                eventName: Event.INLINE_ACTION,
                                props: {
                                  actionName: 'Change Capacity',
                                  actionValue: null,
                                },
                              })
                            }
                          }}
                        />
                      )
                    }}
                  </Permissions>
                </div>
                <Tooltip2
                  content={
                    <>
                      <div>You can set the maximum number of people for this neighborhood.</div>
                      <div>This can be greater than the number of physical seats.</div>
                    </>
                  }
                >
                  <div data-cy="capacity_counter" className="text-sm uppercase">
                    CAPACITY
                  </div>
                </Tooltip2>
              </div>
              <Tooltip2
                content={
                  <>
                    <div>
                      The number of employees divided by the capacity, expressed as a percent.
                    </div>
                    <div>
                      The effective or virtual occupancy, disregarding the number of physical seats.
                    </div>
                  </>
                }
              >
                <OccupancyCounter occupancy={neighborhood.item.counters.occupancy} />
              </Tooltip2>
            </div>
          )}
        </>
      )}
      <EmployeesManageSection neighborhood={neighborhood} />
      <SpacesManageSection neighborhood={neighborhood} />
    </div>
  )
}

function CounterComp({
  value,
  label,
  label2,
  data,
}: {
  value: string | number
  label: string
  label2?: string
  data?: string
}) {
  return (
    <div data-cy={data}>
      <div className="font-medium mb-2 text-2xl">{value}</div>
      <div className="text-sm uppercase">{label}</div>
      {label2 && <div className="text-sm uppercase">{label2}</div>}
    </div>
  )
}

function DialogToolbar({ onDelete, onClose }: DialogToolbarProps) {
  return (
    <div className="dialog-buttons" style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {onDelete && (
        <Confirmation
          text="Delete this neighborhood?"
          onConfirm={() => {
            onDelete()
            track.neighborhoodDialog({
              eventName: Event.DELETE_RECORD_DIALOG,
            })
          }}
        >
          <IconButton minimal tooltip="Delete neighborhood" iconName="trash" />
        </Confirmation>
      )}
      {onClose && <IconButton minimal tooltip="Close" iconName="times" onClick={onClose} />}
    </div>
  )
}

function EmployeesAndSeatsCounter({
  employees,
  workstations,
}: {
  employees: number
  workstations: number
}) {
  return (
    <CounterComp
      value={`${employees} : ${workstations}`}
      label={'employees : seats'}
      data="employees_seats_counter"
    />
  )
}

function SharingRatioCounter({ value }: { value: number }) {
  return (
    <CounterComp
      value={Number(value.toFixed(2))}
      label="SHARING RATIO"
      label2="EMPLOYEES/SEATS"
      data="sharing_ratio_counter"
    />
  )
}

function OccupancyCounter({ occupancy }: { occupancy: number }) {
  return (
    <CounterComp
      value={String(occupancy) + '%'}
      label="OCCUPANCY"
      label2="EMPLOYEES/CAPACITY"
      data="occupancy_counter"
    />
  )
}
export default NDD
